<template>
    <div class="article-attribute-list" v-if="orderedAttributes.length > 0">
        <span
            v-for="attribute in orderedAttributes"
            :key="attribute.id"
            class="attribute"
            :class="{ exclude: attribute.effect === AttributeEffect.Exclude }"
        >
            <i v-if="attribute.effect === AttributeEffect.Exclude" class="fas fa-minus-circle"></i>
            {{ attributeName(attribute) }}
        </span>
    </div>
</template>

<script setup lang="ts">
    import type { ICustomerArticleAttribute } from "@/data/model/Customer";
    import { AttributeEffect } from "@/data/model/Enums";
    import { useArticleAttributesStore } from "@/data/stores/articleAttribute";
    import { Format } from "@/data/util/Format";
    import { clientLanguage } from "@/shared/localization/language";
    import { computed, ref, watch } from "vue";

    const props = defineProps<{
        attributes?: Array<ICustomerArticleAttribute>;
    }>();

    const attributeStore = useArticleAttributesStore();

    const currentAttributes = ref<ICustomerArticleAttribute[]>(props.attributes ?? []);

    // an article carries its attributes in the order they were linked, the store holds the location wide
    // order every frontend renders them in
    const orderedAttributes = computed(() => {
        const rankById = new Map(attributeStore.orderedAttributes.map((attribute, index) => [attribute.id, index]));
        const rankOf = (attribute: ICustomerArticleAttribute) => rankById.get(attribute.id) ?? Number.MAX_VALUE;

        return [...currentAttributes.value].sort((a, b) => rankOf(a) - rankOf(b));
    });

    // the attributes can either be bound as a property (vue hosts) or pushed in
    // with setAttributes (hosts that mount this component imperatively)
    watch(
        () => props.attributes,
        (newAttributes) => {
            currentAttributes.value = newAttributes ?? [];
        }
    );

    function attributeName(attribute: ICustomerArticleAttribute) {
        return Format.translate(attribute.name, clientLanguage()) ?? "";
    }

    function setAttributes(newAttributes: Array<ICustomerArticleAttribute>) {
        currentAttributes.value = newAttributes;
    }

    defineExpose({
        setAttributes,
    });
</script>

<style scoped lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";

    .article-attribute-list {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        padding: 8px 0;
    }

    .attribute {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 10px 10px;
        border-radius: 999px;
        color: var(--colorPrimary);
        background-color: #fff;
        border: 1px solid var(--colorPrimary);
        font-size: 14px;
        line-height: 12px;

        &.exclude {
            color: colors.$red;
            background-color: #fff;
            border: 1px solid colors.$red;
        }

        > i {
            font-size: 14px;
        }
    }
</style>
